/**
 * Relation-type editable cell inputs.
 * Handles: multi-user, user, ticket, company, contact, email-aliases.
 * All relation inputs use auto-save (no manual save/cancel flow).
 */

import React from "react";
import { MultiUserInlineSelect } from "@/components/ui/selects/components/MultiUserInlineSelect";
import { UserSelect } from "@/components/shared/UserSelect";
import { TicketSelectWithModal } from "@/components/ui/selects/components/TicketSelectWithModal";
import { CompanySelectWithModal } from "@/components/ui/selects/components/CompanySelectWithModal";
import { ClientSelectWithModal } from "@/components/ui/selects/components/ClientSelectWithModal";
import { EmailAliasesEditor } from "@/features/contacts/components/EmailAliasesEditor";
import { createAutoSaveHandler } from "../../../utils/inline-edit-controls";
import { normalizeMultiUserData } from "../../../utils/unified-field-renderer";
import type { EditableCellEditInputsProps } from "../types";
import type { BaseEntity } from "../../../types/index";

type RelationInputsProps<T extends BaseEntity = BaseEntity> = Pick<
  EditableCellEditInputsProps<T>,
  | "value"
  | "fieldConfig"
  | "rowId"
  | "fieldId"
  | "editValue"
  | "data"
  | "onCancel"
  | "setEditValue"
  | "onInlineEdit"
>;

/** Multi-user assignment input (auto-save). */
export function MultiUserInput<T extends BaseEntity = BaseEntity>({
  value,
  fieldConfig,
  rowId,
  fieldId,
  data,
  onInlineEdit,
  onCancel,
}: RelationInputsProps<T>): React.ReactNode {
  const autoSaveParams = { rowId, fieldId, data, fieldConfig, onInlineEdit, onCancel };
  return (
    <div className="relative z-50 w-full min-w-0">
      <MultiUserInlineSelect
        currentUserIds={normalizeMultiUserData(value)}
        onUsersSelect={createAutoSaveHandler(autoSaveParams)}
        showUnassigned={fieldConfig.multiUserConfig?.showUnassigned ?? true}
        maxUsers={fieldConfig.multiUserConfig?.maxUsers ?? 5}
        placeholder={fieldConfig.placeholder || "Assign to users..."}
        pageSize={30}
      />
    </div>
  );
}

/** Single user assignment input (auto-save). */
export function UserInput<T extends BaseEntity = BaseEntity>({
  fieldConfig,
  rowId,
  fieldId,
  editValue,
  data,
  onInlineEdit,
  onCancel,
  setEditValue,
}: RelationInputsProps<T>): React.ReactNode {
  const autoSaveParams = { rowId, fieldId, data, fieldConfig, onInlineEdit, onCancel, setEditValue };
  return (
    <div className="relative z-50 w-full min-w-0">
      <UserSelect
        currentUserId={editValue}
        onUserSelect={createAutoSaveHandler(autoSaveParams)}
        showUnassigned={true}
        showAllUsers={false}
        className="min-w-[120px] h-8"
        expandInline={true}
        expandInlineVariant="replace"
      />
    </div>
  );
}

/** Ticket relation input (auto-save). */
export function TicketInput<T extends BaseEntity = BaseEntity>({
  fieldConfig,
  rowId,
  fieldId,
  editValue,
  data,
  onInlineEdit,
  onCancel,
  setEditValue,
}: RelationInputsProps<T>): React.ReactNode {
  const autoSaveParams = { rowId, fieldId, data, fieldConfig, onInlineEdit, onCancel, setEditValue };
  return (
    <div className="relative z-50 w-full min-w-0">
      <TicketSelectWithModal
        value={editValue}
        onValueChange={createAutoSaveHandler(autoSaveParams)}
        placeholder="Select ticket..."
        fullWidth={true}
        highZIndex={true}
        expandInline={true}
        expandInlineVariant="replace"
      />
    </div>
  );
}

/** Company relation input (auto-save). */
export function CompanyInput<T extends BaseEntity = BaseEntity>({
  fieldConfig,
  rowId,
  fieldId,
  editValue,
  data,
  onInlineEdit,
  onCancel,
  setEditValue,
}: RelationInputsProps<T>): React.ReactNode {
  const autoSaveParams = { rowId, fieldId, data, fieldConfig, onInlineEdit, onCancel, setEditValue };
  return (
    <div className="relative z-50 w-full min-w-0">
      <CompanySelectWithModal
        value={editValue}
        onValueChange={createAutoSaveHandler(autoSaveParams)}
        placeholder="Search companies..."
        highZIndex={true}
        expandInline={true}
        expandInlineVariant="replace"
      />
    </div>
  );
}

/** Contact/client relation input (auto-save). */
export function ContactInput<T extends BaseEntity = BaseEntity>({
  fieldConfig,
  rowId,
  fieldId,
  editValue,
  data,
  onInlineEdit,
  onCancel,
  setEditValue,
}: RelationInputsProps<T>): React.ReactNode {
  const autoSaveParams = { rowId, fieldId, data, fieldConfig, onInlineEdit, onCancel, setEditValue };
  return (
    <div className="relative z-50 w-full min-w-0">
      <ClientSelectWithModal
        value={editValue}
        onValueChange={createAutoSaveHandler(autoSaveParams)}
        placeholder="Select contact..."
        fullWidth={true}
        expandInline={true}
        expandInlineVariant="replace"
        highZIndex={true}
      />
    </div>
  );
}

/** Email aliases editor (auto-save via onCancel after update). */
export function EmailAliasesInput<T extends BaseEntity = BaseEntity>({
  rowId,
  data,
  onCancel,
}: RelationInputsProps<T>): React.ReactNode {
  return (
    <div className="relative z-50">
      <EmailAliasesEditor
        contact={data.find((item) => item.id === rowId) as any}
        onUpdate={onCancel}
      />
    </div>
  );
}
